import React, { useMemo } from 'react';
import { MatterDetailed } from '../../../types/matters';
import MatterCard from './MatterCard';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

interface MattersKanbanViewProps {
  matters: MatterDetailed[];
  loading: boolean;
  onMatterClick: (matter: MatterDetailed) => void;
  onStatusChange?: (matterId: string, newStatus: 'active' | 'pending') => void;
}

const statusOrder = ['active', 'pending'] as const;

type StatusType = typeof statusOrder[number];

const MattersKanbanView: React.FC<MattersKanbanViewProps> = ({
  matters,
  loading,
  onMatterClick,
  onStatusChange
}) => {
  // Filter to only show active and pending engagements for Kanban (not closed)
  const kanbanMatters = useMemo(() => {
    return matters.filter(matter => matter.status === 'active' || matter.status === 'pending');
  }, [matters]);

  // Group engagements by status
  const mattersByStatus = useMemo(() => {
    const grouped: Record<StatusType, MatterDetailed[]> = {
      active: [],
      pending: []
    };
    kanbanMatters.forEach(matter => {
      if (grouped[matter.status as StatusType]) {
        grouped[matter.status as StatusType].push(matter);
      }
    });
    return grouped;
  }, [kanbanMatters]);

  const getStatusConfig = (status: StatusType) => {
    switch (status) {
      case 'active':
        return {
          title: 'Active Engagements',
          color: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-700',
          textColor: 'text-green-800 dark:text-green-200',
          icon: '⚖️'
        };
      case 'pending':
        return {
          title: 'Pending Engagements',
          color: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-700',
          textColor: 'text-yellow-800 dark:text-yellow-200',
          icon: '⏳'
        };
      default:
        return {
          title: status,
          color: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-700',
          textColor: 'text-gray-800 dark:text-gray-200',
          icon: '📁'
        };
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    const sourceStatus = source.droppableId as StatusType;
    const destStatus = destination.droppableId as StatusType;
    if (sourceStatus !== destStatus && onStatusChange) {
      onStatusChange(draggableId, destStatus);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading engagements...</span>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {statusOrder.map((status) => {
          const config = getStatusConfig(status);
          const statusMatters = mattersByStatus[status];
          return (
            <Droppable droppableId={status} key={status}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`${config.color} ${config.borderColor} border rounded-lg p-4 min-h-[600px] transition-shadow ${snapshot.isDraggingOver ? 'ring-2 ring-blue-400' : ''}`}
                >
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{config.icon}</span>
                      <h3 className={`text-lg font-semibold ${config.textColor}`}>{config.title}</h3>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.color} ${config.textColor} border ${config.borderColor}`}>
                      {statusMatters.length}
                    </span>
                  </div>

                  {/* Status Description */}
                  <div className={`text-sm ${config.textColor} mb-4 opacity-75`}>
                    {status === 'active' && 'Engagements currently being worked on'}
                    {status === 'pending' && 'Engagements awaiting action or approval'}
                  </div>

                  {/* Matters in this column */}
                  <div className="space-y-3">
                    {statusMatters.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 dark:text-gray-500">
                          <svg className="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <p className="text-sm">No {status} engagements</p>
                        </div>
                      </div>
                    ) : (
                      statusMatters.map((matter, idx) => (
                        <Draggable draggableId={matter.id} index={idx} key={matter.id}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`w-full ${snapshot.isDragging ? 'ring-2 ring-blue-400 shadow-lg' : ''} transition-shadow`}
                            >
                              <MatterCard
                                matter={matter}
                                onClick={() => onMatterClick(matter)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>

      {/* Info Footer */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-blue-800 dark:text-blue-200">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Drag and drop engagements between columns to change their status. Closed engagements are not shown in this view.</span>
        </div>
      </div>
    </DragDropContext>
  );
};

export default MattersKanbanView;
